/**
 * @Relay no-runtime
 */
import { useCallback } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import graphql from 'graphql-tag';

import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop';
import { useReadFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop';
import { expValEquals } from '@confluence/feature-experiments';

import type { ContentBodyType, ContentNodeType } from '../__types__';
import type { useReadAndWriteContentQueryDataCacheReadContent$data } from './__generated__/useReadAndWriteContentQueryDataCacheReadContent.graphql';
import type { useReadAndWriteContentQueryDataCacheWriteContent$data } from './__generated__/useReadAndWriteContentQueryDataCacheWriteContent.graphql';

export const useReadAndWriteContentQueryDataCache = ({ query, contentId, spaceKey }) => {
	const client = useApolloClient();
	const readQueryInterop =
		useReadFragmentInterop<useReadAndWriteContentQueryDataCacheReadContent$data>({
			fragmentSpec: graphql`
				fragment useReadAndWriteContentQueryDataCacheReadContent on Content {
					body {
						dynamic {
							representation
							value
							webresource {
								superbatch {
									tags {
										js
										css
									}
								}
								tags {
									js
									css
								}
							}
						}
					}
				}
			`,
		});
	const writeFragmentInterop =
		useWriteFragmentInterop<useReadAndWriteContentQueryDataCacheWriteContent$data>({
			fragmentSpec: graphql`
				fragment useReadAndWriteContentQueryDataCacheWriteContent on Content {
					body {
						dynamic {
							representation
							value
							webresource {
								superbatch {
									tags {
										js
										css
									}
								}
								tags {
									js
									css
								}
							}
						}
					}
				}
			`,
		});

	const readAndWriteQueryCache = useCallback(
		(newAdfDoc) => {
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			const isSSR = Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__);

			const contentUnifiedQueryVariables = {
				contentId,
				spaceKey,
				versionOverride: null,
				isSSR,
				useNewContentTopper: true,
			};

			let contentQueryResultCache;

			if (expValEquals('remove_usages_of_cache_read_write', 'isEnabled', true)) {
				contentQueryResultCache = readQueryInterop({
					id: `Content:${contentId}`,
				});
			} else {
				// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
				contentQueryResultCache = client.readQuery({
					query,
					variables: contentUnifiedQueryVariables,
				});
			}

			const content: ContentNodeType = expValEquals(
				'remove_usages_of_cache_read_write',
				'isEnabled',
				true,
			)
				? contentQueryResultCache
				: contentQueryResultCache?.content?.nodes?.[0] || null;
			const contentBody: ContentBodyType = content?.body?.dynamic || null;

			if (contentBody) {
				contentBody.value = JSON.stringify(newAdfDoc);

				if (expValEquals('remove_usages_of_cache_read_write', 'isEnabled', true)) {
					writeFragmentInterop({
						fragmentId: `Content:${contentId}`,
						data: contentQueryResultCache,
					});
				} else {
					// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
					client.writeQuery({
						query,
						variables: contentUnifiedQueryVariables,
						data: contentQueryResultCache,
					});
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[query, contentId, spaceKey, readQueryInterop, writeFragmentInterop],
	);

	return readAndWriteQueryCache;
};
